/* =============================================================================
   The design system's own strings, in Brazilian Portuguese
   -----------------------------------------------------------------------------
   Typed as `Record<DsTranslationKey, string>` rather than left to inference, so
   a key added to the English catalogue and forgotten here fails the type check
   instead of falling back to English at run time — a half-translated screen is
   the failure this catches, and it is invisible in review.

   Wording follows the register of the product's audience: a construction and
   investor-reporting tool, addressed as `você`, in the vocabulary a Brazilian
   engineer or controller uses — `idioma`, `notificações`, `sair`.
   ============================================================================= */

import type { DsTranslationKey } from './en';

export const ptBR: Record<DsTranslationKey, string> = {
  'ds.header.home': 'Página inicial do escrowbuild.io',
  'ds.header.language': 'Idioma',
  'ds.header.languageNamed': 'Idioma: {language}',
  'ds.empty.clearFilters': 'Limpar filtros',
  'ds.header.account': 'Conta',
  'ds.header.accountMenu': 'Conta e idioma',
  'ds.header.profile': 'Perfil',
  'ds.header.signOut': 'Sair',
  'ds.header.notificationsUnread': 'Notificações não lidas: {count}',
  'ds.header.accountNamed': 'Conta: {name}',
  'ds.form.required': 'Este campo é obrigatório',
  'ds.form.invalidEmail': 'Informe um e-mail válido',
  'ds.header.languageOptionCurrent': '{language} (atual)',
  'ds.theme.light': 'Claro',
  'ds.theme.dark': 'Escuro',
  'ds.theme.system': 'Sistema',
  'ds.theme.name': 'Tema: {current}. Mudar para {next}.',
  'ds.theme.name.light': 'claro',
  'ds.theme.name.dark': 'escuro',
  'ds.theme.name.system': 'seguir o sistema',
  'ds.metric.trend.up': 'alta',
  'ds.metric.trend.down': 'queda',
  'ds.metric.trend.flat': 'estável',
  'ds.phase.planning': 'Planejamento e pré-obra',
  'ds.phase.planning.short': 'Planejamento',
  'ds.phase.acquisition': 'Aquisição e mobilização',
  'ds.phase.acquisition.short': 'Aquisição',
  'ds.phase.demolition': 'Demolição e preparação do terreno',
  'ds.phase.demolition.short': 'Demolição',
  'ds.phase.foundation': 'Fundação',
  'ds.phase.foundation.short': 'Fundação',
  'ds.phase.framing': 'Estrutura e telhado',
  'ds.phase.framing.short': 'Estrutura',
  'ds.phase.utilities': 'Instalações e vedação',
  'ds.phase.utilities.short': 'Instalações',
  'ds.phase.finishes': 'Acabamentos',
  'ds.phase.finishes.short': 'Acabamentos',
  'ds.phase.landscaping': 'Paisagismo',
  'ds.phase.landscaping.short': 'Paisagismo',
  'ds.phase.completion': 'Conclusão e venda',
  'ds.phase.completion.short': 'Conclusão',

  'ds.chart.series.budgeted': 'Orçado',
  'ds.chart.series.actual': 'Realizado',
  'ds.chart.bar.title': 'Orçamento por etapa',
  'ds.chart.bar.description': 'Orçado contra o custo realizado de cada etapa contratada.',
  'ds.chart.bar.summary': 'Orçamento de {phases} etapas da obra. O total orçado é {budgeted}; o realizado é {actual}.',
  'ds.chart.bar.chartLabel': 'Gráfico de barras do custo orçado contra o realizado em {phases} etapas',
  'ds.chart.bar.legend': 'Séries de orçamento',
  'ds.chart.bar.empty': 'Todas as séries de orçamento estão ocultas. Reative uma na legenda.',
  'ds.chart.bar.tableCaption': 'Orçado contra o realizado, por etapa',
  'ds.chart.bar.columnPhase': 'Etapa',
  'ds.chart.line.title': 'Gasto por mês',
  'ds.chart.line.description': 'Orçado contra o realizado, de janeiro a setembro de 2026.',
  'ds.chart.line.summary': 'Gasto mensal ao longo de {months} meses. O total orçado é {budgeted}; o realizado é {actual}.',
  'ds.chart.line.chartLabel': 'Gráfico de linhas do gasto mensal, orçado contra o realizado, em {months} meses',
  'ds.chart.line.legend': 'Séries de gasto',
  'ds.chart.line.empty': 'Todas as séries de gasto estão ocultas. Reative uma na legenda.',
  'ds.chart.line.tableCaption': 'Gasto por mês, orçado contra o realizado',
  'ds.chart.line.columnMonth': 'Mês',
  'ds.header.notifications': 'Notificações',

  /* PaginatedTable */
  'ds.table.firstPage': 'Primeira página',
  'ds.table.previousPage': 'Página anterior',
  'ds.table.nextPage': 'Próxima página',
  'ds.table.lastPage': 'Última página',
  'ds.table.displaying.one': 'Exibindo {from}–{to} de {total} linha',
  'ds.table.displaying.other': 'Exibindo {from}–{to} de {total} linhas',
  'ds.table.rowsPerPage': 'Linhas por página',
  'ds.table.goToPage': 'Ir para a página',
  'ds.table.go': 'Ir',
  'ds.table.page.one': 'Página {current} de {last}',
  'ds.table.page.other': 'Página {current} de {last}',
  'ds.table.columnsEnabled.one': '{visible} de {total} coluna ativada',
  'ds.table.columnsEnabled.other': '{visible} de {total} colunas ativadas',
  'ds.table.closePanel': 'Fechar painel',
  'ds.table.chooseColumns': 'Escolher colunas',
  'ds.table.columns': 'Colunas',
  'ds.table.columnsPanelDescription': 'Escolha quais colunas são exibidas. Sua escolha fica salva.',
  'ds.table.alwaysVisible': 'sempre visível',
  'ds.table.filterColumn': 'Filtrar {column}',
  'ds.table.filterColumnActive': 'Filtrar {column} (filtro ativo)',
  'ds.table.enterBothBounds': 'Informe os dois limites.',
  'ds.table.clear': 'Limpar',
  'ds.table.apply': 'Aplicar',
  'ds.table.from': 'De',
  'ds.table.to': 'Até',
  'ds.table.fromDate': 'Data inicial',
  'ds.table.toDate': 'Data final',
  'ds.table.date': 'Data',
  'ds.table.time': 'Hora',
  'ds.table.timePlaceholder': 'HH:MM',
  'ds.table.search': 'Pesquisar…',
  'ds.table.selectedValues': 'Valores selecionados',
  'ds.table.removeValue': 'Remover {value}',
  'ds.table.minSearchLength.one': 'Digite pelo menos {count} caractere.',
  'ds.table.minSearchLength.other': 'Digite pelo menos {count} caracteres.',
  'ds.table.searching': 'Buscando…',
  'ds.table.noMatches': 'Nenhum resultado.',
  'ds.table.refineSearch': 'Refine sua busca — há mais resultados.',
  'ds.table.noSearchSource': 'Não há fonte de busca configurada para esta coluna.',
  'ds.table.filters': 'Filtros:',
  'ds.table.clearAllFilters': 'Limpar todos os filtros',
  'ds.table.scrollForMoreColumns': 'Role para ver mais colunas',
  'ds.table.sortBy': 'Ordenar por',
  'ds.table.subtotal': 'Subtotal',
  'ds.stepper.position': 'Etapa {current} de {total}',
  'ds.breadcrumb.label': 'Trilha de navegação',
  'ds.alert.dismiss': 'Dispensar',
  'ds.toast.region': 'Notificações',
  'ds.toast.dismiss': 'Dispensar notificação',
  'ds.table.aggSum': 'Soma',
  'ds.table.aggAvg': 'Média',
  'ds.table.aggMin': 'Mín',
  'ds.table.aggMax': 'Máx',
  'ds.table.aggCount': 'Contagem',
  'ds.table.aggDistinct': 'Distintos',
  'ds.table.aggMedian': 'Mediana',
  'ds.table.aggStddev': 'Desvio padrão',
  'ds.table.announceLoading': 'Carregando linhas.',
  'ds.table.announceError': 'Erro. {message}',
  'ds.table.announceNoRowsFiltered': 'Nenhuma linha corresponde aos filtros ativos.',
  'ds.table.announceNoRows': 'Nenhuma linha.',
  'ds.table.announceDisplaying.one': 'Exibindo {shown} de {total} linha.',
  'ds.table.announceDisplaying.other': 'Exibindo {shown} de {total} linhas.',
  'ds.table.errorBothBounds': 'Informe os dois limites para aplicar este filtro.',
  'ds.table.errorStartDate': 'Informe uma data inicial válida.',
  'ds.table.errorEndDate': 'Informe uma data final válida.',
  'ds.table.errorStartBeforeEnd': 'A data inicial não pode ser posterior à data final.',
  'ds.table.errorStartTime': 'Informe um horário inicial válido (HH:MM).',
  'ds.table.errorEndTime': 'Informe um horário final válido (HH:MM).',
  'ds.table.errorStartAfterEnd': 'O início não pode ser posterior ao fim.',
  'ds.table.errorLowerBound': 'Informe um limite inferior válido.',
  'ds.table.errorUpperBound': 'Informe um limite superior válido.',
  'ds.table.errorPercentageRange': 'Informe um percentual entre 0 e 100.',
  'ds.table.errorBoundsOrder': 'O limite inferior não pode ser maior que o limite superior.',
  'ds.table.errorValidDate': 'Informe uma data válida.',
  'ds.table.errorValidTime': 'Informe um horário válido (HH:MM).',
  'ds.table.errorValidNumber': 'Informe um número válido.',
  'ds.table.errorValidValue': 'Informe um valor válido.',

  /* NotificationsList */
  'ds.notifications.unread': 'Não lida',
  'ds.notifications.unreadCount.one': '{count} não lida',
  'ds.notifications.unreadCount.other': '{count} não lidas',
  'ds.notifications.noneUnread': 'Nenhuma notificação não lida',
  'ds.notifications.markAllRead': 'Marcar todas como lidas',
  'ds.notifications.errorTitle': 'Não foi possível carregar as notificações',
  'ds.notifications.emptyTitle': 'Nenhuma notificação',
  'ds.notifications.emptyDescription':
    'Atualizações do empreendimento, aprovações e alertas aparecem aqui.',

  /* InputNumber */
  'ds.table.errorFallback': 'A fonte de dados não respondeu.',
  'ds.number.decrease': 'Diminuir',
  'ds.number.increase': 'Aumentar',
  'ds.number.saved': 'Salvo!',

  /* ChartFrame, Select, PdfPreview, Link */
  'ds.chart.dataTable': 'Tabela de dados',

  'ds.select.searching': 'Buscando…',
  'ds.select.noMatches': 'Nenhum resultado encontrado.',
  'ds.select.noMatchesFor': 'Nenhum resultado para “{query}”.',
  'ds.select.truncatedHint':
    'Mostrando as correspondências mais próximas — continue digitando para reduzir a lista.',
  'ds.select.minLength.one': 'Digite mais {count} caractere para buscar.',
  'ds.select.minLength.other': 'Digite mais {count} caracteres para buscar.',

  'ds.pdf.zoomIn': 'Ampliar',
  'ds.pdf.zoomOut': 'Reduzir',
  'ds.pdf.resetZoom': 'Restaurar o zoom em 100%',
  'ds.pdf.previousPage': 'Página anterior',
  'ds.pdf.nextPage': 'Próxima página',
  'ds.pdf.pageOf': 'Página {current} de {total}',
  'ds.pdf.loading': 'Carregando documento…',
  'ds.pdf.loadingLabel': 'Carregando documento',
  'ds.pdf.errorTitle': 'O documento não carregou',

  'ds.link.newTab': '(abre em uma nova aba)',
  'ds.search.clear': 'Limpar busca',
  'ds.search.label': 'Buscar',
};
